<template>
  <CDialog v-model="isOpen">
    <header class="dialog-header align-items-start">
      <div class="flex-fill">
        <h1 class="h4 mrgv0">
          <h1 class="h4 mrgv0">
            {{ title }}
          </h1>
          <h2 v-if="group && group.name" class="h5 text-alt mrgt-- mrgb0">
            {{ group.name }}
          </h2>
        </h1>
      </div>

      <Btn variant="icon" @click="isOpen = !isOpen">
        <Icon id="close" :scale="0.75" />
      </Btn>
    </header>

    <main class="dialog-body">
      <div class="mrgb+">
        {{ $t('planification.groups.description') }}
      </div>

      <ValidationObserver>
        <Form @passed="submit">
          <section class="mrgb+">
            <FormGroup v-bind="fields.name">
              <Input :id="fields.name.id" v-model="form.name" />
            </FormGroup>
            <FormGroup v-show="group" v-bind="fields.primary_call">
              <Select
                :id="fields.primary_call.id"
                v-model="form.primary_call"
                :options="availableMembers"
                label="name"
              />
            </FormGroup>
          </section>

          <section class="mrgb+">
            <div class="flex align-items-center relative-parent mrgb-">
              <h5 class="mrgv0">
                {{ $t('planification.groups.interval.title') }}
              </h5>

              <Btn variant="icon" @click="intervalPopover = !intervalPopover">
                <Icon id="help" :scale="0.75" class="mrgl--" />
              </Btn>

              <Popover
                v-if="intervalPopover"
                position="bottom start"
                class="popover--full"
              >
                <div class="popover__body">
                  {{ $t('planification.groups.interval.description') }}
                </div>
              </Popover>
            </div>

            <Grid multiline>
              <GridItem :widths="{ default: '1/1' }">
                <FormGroup v-bind="fields.startDate">
                  <Datepicker v-model="form.startDate" mode="dateTime" />
                </FormGroup>
              </GridItem>

              <GridItem :widths="{ default: '1/1' }">
                <FormGroup v-bind="fields.endDate">
                  <Datepicker v-model="form.endDate" mode="dateTime" />
                </FormGroup>
              </GridItem>
            </Grid>
          </section>

          <section v-if="!group" class="mrgb+">
            <div class="flex align-items-center relative-parent mrgb-">
              <h5 class="mrgv0">
                {{ $t('planification.groups.hierarchy.title') }}
              </h5>

              <Btn variant="icon" @click="hierarchyPopover = !hierarchyPopover">
                <Icon id="help" :scale="0.75" class="mrgl--" />
              </Btn>

              <Popover
                v-if="hierarchyPopover"
                position="bottom start"
                class="popover--full"
              >
                <div class="popover__body">
                  {{ $t('planification.groups.hierarchy.description') }}
                </div>
              </Popover>
            </div>

            <FormGroup v-bind="fields.parentGroup">
              <Select
                :id="fields.parentGroup.id"
                v-model="form.parentGroup"
                :options="availableParentGroups"
                label="name"
              />
            </FormGroup>
          </section>

          <section>
            <div class="flex align-items-center relative-parent mrgb-">
              <h5 class="mrgv0">
                {{ $t('planification.groups.directory.title') }}
              </h5>

              <Btn variant="icon" @click="directoryPopover = !directoryPopover">
                <Icon id="help" :scale="0.75" class="mrgl--" />
              </Btn>

              <Popover
                v-if="directoryPopover"
                position="bottom start"
                class="popover--full"
              >
                <div class="popover__body">
                  {{ $t('planification.groups.directory.description') }}
                </div>
              </Popover>
            </div>

            <FormGroup v-bind="fields.directory">
              <Select
                :id="fields.directory.id"
                v-model="form.directory"
                :options="availableDirectories"
                label="name"
              />
            </FormGroup>
            <label class="form-group-label"> </label>
            <Loader v-if="creatingDirectory" size="small" negative />
            <Btn v-else variant="link" @click="createDirectory()">
              <Icon id="add" :scale="0.6" />
              {{ $t('planification.groups.new-directory') }}
            </Btn>
          </section>

          <ListInline align="center" size="small" class="mrgt+">
            <li>
              <Btn variant="outline" @click="isOpen = false">
                {{ $t('common.cancel') }}
              </Btn>
            </li>
            <li>
              <Btn data-cy="validate-group" type="submit" variant="primary">
                <div class="flex">
                  <Loader v-if="loading" size="small" negative />
                  <template v-else>
                    {{ $t(`common.${group ? 'edit' : 'add'}`) }}
                  </template>
                </div>
              </Btn>
            </li>
          </ListInline>
        </Form>
      </ValidationObserver>
    </main>
  </CDialog>
</template>

<script>
import eventBus from '@/eventBus';
import { ValidationObserver } from 'vee-validate';

import Popover from '@/components/ui/Popover';
import Select from '@/components/ui/Select';
import Loader from '@/components/ui/Loader';

export default {
  components: {
    ValidationObserver,
    Popover,
    Select,
    Loader,
  },

  data() {
    return {
      isOpen: false,
      loading: false,
      creatingDirectory: false,
      intervalPopover: false,
      hierarchyPopover: false,
      directoryPopover: false,
      group: null,
      form: {
        name: null,
        primary_call: null,
        startDate: null,
        endDate: null,
        parentGroup: null,
        directory: null,
      },
    };
  },

  computed: {
    title() {
      return this.$t(
        `planification.groups.${this.group ? 'edit' : 'add'}.title`
      );
    },

    availableParentGroups() {
      return Object.values(this.$store.state.planification.index)
        .map(group => ({
          id: group.id,
          name: group.name,
          username: group.username,
        }))
        .filter(group => group.username !== this.group?.username);
    },

    availableDirectories() {
      return Object.values(this.$store.state.directories.directories).map(
        directory => ({
          id: directory.id,
          name: directory.name,
        })
      );
    },

    availableMembers() {
      if (!this.group) {
        return [];
      }
      return Object.values(this.group.members).map(member => ({
        id: member.id,
        name: member.user?.label,
      }));
    },

    fields() {
      return {
        name: {
          name: 'name',
          id: 'name',
          label: this.$t('planification.groups.fields.name.label'),
          help: this.$t('planification.groups.fields.name.help'),
          rules: 'required',
          required: true,
          mode: 'passive',
          row: true,
        },
        primary_call: {
          name: 'primary_call',
          id: 'primary_call',
          label: this.$t('planification.groups.fields.primary_call.label'),
          help: this.$t('planification.groups.fields.primary_call.help'),
          mode: 'passive',
          row: true,
        },
        startDate: {
          name: 'start-date',
          id: 'start-date',
          label: this.$t('planification.groups.fields.start-datetime.label'),
          mode: 'passive',
          row: true,
        },
        endDate: {
          name: 'end-date',
          id: 'end-date',
          label: this.$t('planification.groups.fields.end-datetime.label'),
          mode: 'passive',
          row: true,
        },
        parentGroup: {
          name: 'parent-group',
          id: 'parent-group',
          label: this.$t('planification.groups.fields.parent-group.label'),
          mode: 'passive',
          row: true,
        },
        directory: {
          name: 'directory',
          id: 'directory',
          label: this.$t('planification.groups.fields.directory.label'),
          mode: 'passive',
          row: true,
        },
      };
    },
  },

  watch: {
    isOpen(isOpen) {
      if (!this.isOpen) {
        this.group = null;
        this.form.name = null;
        this.form.primary_call = null;
        this.form.startDate = null;
        this.form.endDate = null;
        this.form.parentGroup = null;
        this.form.directory = null;
      }
    },
  },

  mounted() {
    eventBus.$on('togglePlanificationGroupAddOrUpdate', this.handleToggle);
  },

  beforeDestroy() {
    eventBus.$off('togglePlanificationGroupAddOrUpdate', this.handleToggle);
  },

  methods: {
    handleToggle(group) {
      if (group) {
        this.group = group;
        this.form.name = group.name;
        this.form.primary_call = this.availableMembers.find(
          member => group.configuration?.primary_call === member.id
        );
        this.form.startDate = group.start_datetime;
        this.form.endDate = group.end_datetime;
        this.form.parentGroup = group.parent_group;
        this.form.directory = this.availableDirectories.find(
          directory => group.configuration?.directory === directory.id
        );
      }
      this.isOpen = !this.isOpen;
    },

    submit() {
      if (this.group) {
        this.handleUpdateGroup();
      } else {
        this.handleAddGroup();
      }
    },

    handleAddGroup() {
      this.loading = true;

      this.$store
        .dispatch('planification/addGroup', {
          name: this.form.name,
          start_datetime: this.form.startDate,
          end_datetime: this.form.endDate,
          parent_group: this.form.parentGroup,
          members: [],
          configuration: {
            directory:
              this.form.directory != null ? this.form.directory.id : null,
            primary_call: this.form.primary_call?.id,
          },
        })
        .then(response => {
          this.isOpen = false;
        })
        .catch(error => {
          this.$notify({
            type: 'error',
            title: this.$t('common.error'),
            text: error.message || error,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    handleUpdateGroup() {
      this.loading = true;

      this.$store
        .dispatch('planification/updateGroup', {
          ...this.group,
          ...{
            name: this.form.name,
            start_datetime: this.form.startDate,
            end_datetime: this.form.endDate,
            previous_parent_group: this.group.parent_group,
            next_parent_group: this.form.parentGroup,
            configuration: {
              directory:
                this.form.directory != null ? this.form.directory.id : null,
              primary_call: this.form.primary_call?.id,
            },
          },
        })
        .then(response => {
          this.isOpen = false;
        })
        .catch(error => {
          this.$notify({
            type: 'error',
            title: this.$t('common.error'),
            text: error.message || error,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    createDirectory() {
      this.creatingDirectory = true;
      this.$store
        .dispatch('directories/createDirectory', {
          directoryName: this.form.name,
        })
        .then(response => {
          this.$store.dispatch('directories/getDirectories').then(() => {
            this.form.directory = response.data;
          });
        })
        .finally(() => {
          this.creatingDirectory = false;
        });
    },
  },
};
</script>
